import type { ApiErrorBody } from '@electrodavisan/shared';
import { config } from '../app/config';

/** Error tipado con el `code` estable que devuelve la API. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | null;
  readonly details: unknown;

  constructor(
    status: number,
    code: string,
    message: string,
    requestId: string | null,
    details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.details = details;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }
}

/**
 * Proveedor del Access Token. Lo inyecta el AuthProvider en el arranque para
 * que este modulo no dependa de React ni guarde el token por su cuenta.
 */
type TokenProvider = () => string | null;

let getAccessToken: TokenProvider = () => null;
let onUnauthorized: () => void = () => undefined;

export function configureApiClient(options: {
  tokenProvider: TokenProvider;
  onUnauthorized: () => void;
}): void {
  getAccessToken = options.tokenProvider;
  onUnauthorized = options.onUnauthorized;
}

const pendingCommands = new Map<string, string>();
async function digest(value: BufferSource): Promise<string> {
  return Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', value)), (b) =>
    b.toString(16).padStart(2, '0'),
  ).join('');
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null | string[]>;
  signal?: AbortSignal;
  /** Para multipart: se envia tal cual, sin Content-Type manual. */
  formData?: FormData;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(`${config.apiUrl}${path}`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue;
      url.searchParams.set(key, Array.isArray(value) ? value.join(',') : String(value));
    }
  }
  return url.toString();
}

async function parseError(response: Response): Promise<ApiError> {
  let code = 'UNKNOWN_ERROR';
  let message = 'No se pudo completar la operacion.';
  let requestId: string | null = response.headers.get('x-request-id');
  let details: unknown;

  try {
    const body = (await response.json()) as ApiErrorBody;
    if (body?.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
      requestId = body.error.requestId ?? requestId;
      details = body.error.details;
    }
  } catch {
    // La respuesta no era JSON (502 del proxy, por ejemplo).
  }

  return new ApiError(response.status, code, message, requestId, details);
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const token = getAccessToken();
  const headers: Record<string, string> = { Accept: 'application/json' };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  let body: BodyInit | undefined;
  if (options.formData) {
    // El navegador pone el boundary correcto: no se toca Content-Type.
    body = options.formData;
  } else if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  let retrySlot: string | undefined;
  if (options.method === 'POST' && /^\/api\/(support\/)?tickets(?:\/|$)/.test(path)) {
    let actor = 'anonymous';
    try {
      actor = JSON.parse(atob(token!.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).sub;
    } catch {
      /* rejected by server */
    }
    let content = typeof body === 'string' ? body : '';
    if (options.formData) {
      const file = options.formData.get('file');
      if (file instanceof File)
        content = file.name + ':' + file.type + ':' + (await digest(await file.arrayBuffer()));
    }
    retrySlot =
      'support-command:' +
      (await digest(new TextEncoder().encode(actor + ':' + path + ':' + content)));
    let key = pendingCommands.get(retrySlot);
    try {
      key ??= localStorage.getItem(retrySlot) ?? sessionStorage.getItem(retrySlot) ?? undefined;
    } catch {
      /* memory fallback */
    }
    key ??= crypto.randomUUID();
    pendingCommands.set(retrySlot, key);
    try {
      localStorage.setItem(retrySlot, key);
    } catch {
      /* memory fallback */
    }
    headers['Idempotency-Key'] = key;
  }

  const response = await fetch(buildUrl(path, options.query), {
    method: options.method ?? 'GET',
    headers,
    body,
    signal: options.signal,
    credentials: 'omit',
  });

  if (response.status === 401) {
    // La sesion ya no vale: se delega en el AuthProvider, que intentara
    // renovarla y, si no puede, llevara de nuevo a Keycloak.
    onUnauthorized();
    throw await parseError(response);
  }

  if (!response.ok) {
    throw await parseError(response);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const payload = (await response.json()) as { success: boolean; data: T };
  if (retrySlot) {
    pendingCommands.delete(retrySlot);
    try {
      localStorage.removeItem(retrySlot);
      sessionStorage.removeItem(retrySlot);
    } catch {
      /* memory fallback */
    }
  }
  return payload.data;
}

/** Descarga un adjunto usando el token (las descargas tambien se autentican). */
export async function downloadFile(path: string, fileName: string): Promise<void> {
  const token = getAccessToken();
  const response = await fetch(buildUrl(path), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    throw await parseError(response);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};
